import React, { useRef, useEffect, useState } from "react";
import { Link } from "gatsby";
import classnames from "classnames";
import IconArrow from "../../assets/IconArrow";
import * as styles from "./Button.module.css";

const Button = ({
  // Core props
  children,
  to = "",
  onClick,
  className,
  
  // Style variants
  variant = "pill", // "pill", "pillSecondary", "text", "round"
  
  // Size variants
  size = "medium", // "small", "medium", "large"
  
  // Round button specific props
  symbol,
  invert,
  
  // Text button specific props
  arrowDirection = "right", // "left", "right"
  
  // Link behavior
  external = false,
  activeClassName,
  partiallyActive,
  
  // Legacy AppLink props for compatibility
  lightOnDark,
  darkOnLight,
  color,
  backgroundColor,
  alt,
  marginTop,
  normalBodyText,
  
  // Accessibility
  ariaLabel,
  
  // Other props
  ...props
}) => {
  const buttonRef = useRef(null);
  const contentRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  
  // Handle content width measurement for round buttons
  useEffect(() => {
    if (variant === "round" && contentRef.current) {
      const width = contentRef.current.scrollWidth;
      setContentWidth(width + 48);
    }
  }, [variant, children]);

  const buttonStyle = variant === "round" ? { '--content-width': `${contentWidth}px` } : {};

  const classes = classnames(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.lightOnDark]: lightOnDark,
      [styles.darkOnLight]: darkOnLight,
      [styles.marginTop]: marginTop,
      [styles.normalBodyText]: normalBodyText,
      [styles.invert]: invert,
      [styles[`color${color ? color.charAt(0).toUpperCase() + color.slice(1) : ''}`]]: color,
      [styles[`background${backgroundColor ? backgroundColor.charAt(0).toUpperCase() + backgroundColor.slice(1) : ''}`]]: backgroundColor,
    },
    className
  );

  const renderContent = () => {
    if (variant === "round") {
      return (
        <>
          {/* Default state symbol/arrow */}
          {symbol ? (
            <div className={`h2 noMargin ${styles.symbol}`}>{symbol}</div>
          ) : (
            <div className={styles.defaultArrow} />
          )}
          
          {/* Expanded content */}
          <span className={styles.buttonContent}>
            <span className={`${styles.buttonText} largestBodyText`}>{children}</span>
            <span className={styles.arrowIcon} />
          </span>
          
          {/* Hidden content for width measurement */}
          <span ref={contentRef} className={styles.hiddenContent}>
            <span className={`${styles.buttonText} largestBodyText`}>{children}</span>
            <span className={styles.arrowIcon} />
          </span>
        </>
      );
    }

    if (variant === "text") {
      return (
        <>
          {arrowDirection === "left" && !external && (
            <span className={styles.iconArrowWrapperReverse}>
              <svg width="39" height="23" viewBox="0 0 39 23" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.iconArrow}>
                <path d="M0 9.47252H0.235C5.1374 9.33208 9.5245 4.89165 9.5245 0H13.5952C13.5952 3.68442 12.1037 7.02819 9.6932 9.47252H39L39 13.5275H9.6932C12.1067 15.9688 13.5952 19.3156 13.5952 23H9.5245C9.5245 18.1084 5.1374 13.6679 0.235 13.5275H0V9.47551V9.47252Z" fill="currentColor"/>
              </svg>
            </span>
          )}
          
          {children}
          
          {arrowDirection === "right" && !external && (
            <span className={styles.iconArrowWrapper}>
              <svg width="39" height="23" viewBox="0 0 39 23" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.iconArrow}>
                <path d="M39 9.47252H38.765C33.8626 9.33208 29.4755 4.89165 29.4755 0H25.4048C25.4048 3.68442 26.8963 7.02819 29.3068 9.47252H0L0 13.5275H29.3068C26.8933 15.9688 25.4048 19.3156 25.4048 23H29.4755C29.4755 18.1084 33.8626 13.6679 38.765 13.5275H39V9.47551V9.47252Z" fill="currentColor"/>
              </svg>
            </span>
          )}
          
          {external && (
            <span className={styles.iconExternalWrapper}>
              <span className={styles.iconExternalBox} />
              <span className={styles.iconExternalArrow}>
                <IconArrow color="offBlack" tail />
              </span>
            </span>
          )}
        </>
      );
    }

    // Default content for pill buttons
    return children;
  };

  // Handle external links - use vanilla <a>
  if (external && to) {
    return (
      <a
        ref={buttonRef}
        href={to}
        className={classes}
        style={buttonStyle}
        onClick={onClick}
        aria-label={ariaLabel}
        target="_blank"
        rel="noreferrer"
        {...props}
      >
        {renderContent()}
      </a>
    );
  }

  // Handle internal links - use Gatsby <Link>
  if (to && !external) {
    return (
      <Link
        ref={buttonRef}
        to={to}
        className={classes}
        style={buttonStyle}
        onClick={onClick}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        aria-label={ariaLabel}
        {...props}
      >
        {renderContent()}
      </Link>
    );
  }

  // Handle button without link - use <button> for JS actions
  return (
    <button
      ref={buttonRef}
      className={classes}
      style={buttonStyle}
      onClick={onClick}
      aria-label={ariaLabel}
      type="button"
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
